import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Pencil,
  Pill,
  Plus,
  Receipt,
  RefreshCw,
  Save,
  Search,
  Shield,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import type { ItemKind } from '@datazo/contracts';
import { Card, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { usePageTitle } from '@/shared/store/page';
import { useToast } from '@/shared/toast/toast-context';
import { useAuthStore } from '@/shared/store/auth';
import { ApiClientError } from '@/shared/api/client';
import {
  useCatalogList,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  type CatalogItem,
  type CatalogKind,
} from './api';

/**
 * Administración del catálogo institucional — insumos + medicamentos.
 *
 * El `codigo` es la clave referencial y es inmutable; las hojas de gasto
 * históricas dependen de él. El equivalente a "borrar" es desactivar.
 * Solo `admin` puede crear/editar/activar — el resto ve la lista en lectura.
 */
export function CatalogPage(): JSX.Element {
  usePageTitle('Catálogo', 'Insumos y medicamentos institucionales');
  const [tab, setTab] = useState<CatalogKind>('insumo');

  return (
    <div className="flex flex-col gap-5 animate-fade-in-up">
      <div className="rounded-md bg-primary-soft border border-primary/20 px-4 py-3 flex items-start gap-3 text-sm text-text-soft">
        <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-text mb-0.5">Catálogo referencial</div>
          <div className="text-xs">
            Los códigos son inmutables — preservan la integridad de las hojas de gasto
            firmadas. Para retirar un ítem del uso clínico, desactívalo (no se borra). Solo
            administradores pueden modificar.
          </div>
        </div>
      </div>

      <Card>
        <Tabs value={tab} onValueChange={(v) => setTab(v as CatalogKind)}>
          <div className="border-b border-border px-4 pt-3">
            <TabsList className="border-b-0">
              <TabsTrigger value="insumo">
                <Receipt className="w-4 h-4 mr-1.5 inline" /> Insumos
              </TabsTrigger>
              <TabsTrigger value="medicamento">
                <Pill className="w-4 h-4 mr-1.5 inline" /> Medicamentos
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="insumo" className="p-0">
            <CatalogTab kind="insumo" />
          </TabsContent>
          <TabsContent value="medicamento" className="p-0">
            <CatalogTab kind="medicamento" />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tab de un kind (insumo o medicamento)                                       */
/* -------------------------------------------------------------------------- */

const PAGE_SIZE = 200;

function CatalogTab({ kind }: { kind: CatalogKind }): JSX.Element {
  const [query, setQuery] = useState<string>('');
  const [debounced, setDebounced] = useState<string>('');
  const [includeInactive, setIncludeInactive] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<CatalogItem | null>(null);

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.rol === 'admin';

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  const { data, isLoading, isFetching, isError, error, refetch } = useCatalogList(kind, {
    q: debounced,
    limit: PAGE_SIZE,
    includeInactive,
  });

  const items = useMemo(() => data ?? [], [data]);
  const activos = items.filter((i) => i.activo).length;
  const inactivos = items.length - activos;

  const titleSingular = kind === 'insumo' ? 'insumo' : 'medicamento';
  const titlePlural = kind === 'insumo' ? 'insumos' : 'medicamentos';

  return (
    <div className="flex flex-col">
      {/* Toolbar */}
      <div className="px-6 pt-5 pb-3 flex items-center gap-2.5 flex-wrap border-b border-border">
        <div className="relative flex-1 min-w-[280px] max-w-[420px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <Input
            placeholder={`Buscar ${titlePlural} por código o nombre…`}
            className="pl-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <Button
          variant={includeInactive ? 'primary' : 'secondary'}
          onClick={() => setIncludeInactive((v) => !v)}
        >
          {includeInactive ? (
            <>
              <ToggleRight className="w-4 h-4" /> Incluye inactivos
            </>
          ) : (
            <>
              <ToggleLeft className="w-4 h-4" /> Solo activos
            </>
          )}
        </Button>
        <div className="flex-1" />
        {isAdmin && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4" /> Nuevo {titleSingular}
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="px-6 py-3 flex items-center gap-4 text-xs text-text-muted border-b border-border bg-surface-alt">
        <span>
          <strong className="text-text font-semibold">{items.length}</strong> resultado
          {items.length === 1 ? '' : 's'}
        </span>
        <span className="text-green-700">
          <strong className="font-semibold">{activos}</strong> activo{activos === 1 ? '' : 's'}
        </span>
        {includeInactive && (
          <span className="text-text-soft">
            <strong className="font-semibold">{inactivos}</strong> inactivo
            {inactivos === 1 ? '' : 's'}
          </span>
        )}
        {isFetching && !isLoading && <span className="italic">actualizando…</span>}
      </div>

      {/* Error */}
      {isError && (
        <div className="bg-danger-soft border-b border-red-200 text-red-800 px-6 py-3 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold">No fue posible cargar el catálogo</div>
            <div className="text-sm text-red-700">{error.message}</div>
          </div>
          <Button size="sm" variant="secondary" onClick={() => void refetch()}>
            <RefreshCw className="w-4 h-4" /> Reintentar
          </Button>
        </div>
      )}

      {/* Tabla */}
      {isLoading ? (
        <div className="p-6 space-y-2" aria-busy="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-10 rounded bg-surface-alt animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center flex flex-col items-center gap-3 text-text-muted">
          {kind === 'insumo' ? (
            <Receipt className="w-8 h-8 opacity-50" />
          ) : (
            <Pill className="w-8 h-8 opacity-50" />
          )}
          <div className="text-text font-semibold">
            {debounced !== '' ? 'Sin coincidencias' : `Aún no hay ${titlePlural}`}
          </div>
          {isAdmin && debounced === '' && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4" /> Crear el primero
            </Button>
          )}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead className="w-[110px]">Estado</TableHead>
              {isAdmin && <TableHead className="w-[60px]"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody className="stagger-children">
            {items.map((item) => (
              <TableRow
                key={`${item.tipo}-${item.codigo}`}
                className={cn(
                  'animate-fade-in-up hover:bg-surface-alt transition-colors',
                  !item.activo && 'opacity-60',
                )}
              >
                <TableCell>
                  <code className="font-mono text-xs bg-surface-alt px-2 py-1 rounded">
                    {item.codigo}
                  </code>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-text">{item.nombre}</div>
                </TableCell>
                <TableCell>
                  {item.activo ? (
                    <Badge variant="green">
                      <CheckCircle2 className="w-3 h-3" /> Activo
                    </Badge>
                  ) : (
                    <Badge variant="gray">Inactivo</Badge>
                  )}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <RowActions kind={kind} item={item} onEdit={() => setEditing(item)} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CardFooter>
        <div className="text-text-muted text-xs">
          Mostrando hasta {PAGE_SIZE} resultados. Refina la búsqueda si necesitas filtrar más.
        </div>
      </CardFooter>

      {createOpen && (
        <CreateItemDialog
          kind={kind}
          open={createOpen}
          onOpenChange={setCreateOpen}
        />
      )}
      {editing !== null && (
        <EditItemDialog
          kind={kind}
          item={editing}
          open={editing !== null}
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Quick row actions                                                          */
/* -------------------------------------------------------------------------- */

function RowActions({
  kind,
  item,
  onEdit,
}: {
  kind: CatalogKind;
  item: CatalogItem;
  onEdit: () => void;
}): JSX.Element {
  const update = useUpdateCatalogItem(kind);
  const { showToast } = useToast();

  const toggle = async (): Promise<void> => {
    try {
      await update.mutateAsync({ codigo: item.codigo, body: { activo: !item.activo } });
      showToast({
        kind: 'success',
        title: !item.activo ? 'Ítem reactivado' : 'Ítem desactivado',
        description: `${item.codigo} · ${item.nombre}`,
      });
    } catch (err) {
      showToast({
        kind: 'danger',
        title: 'No fue posible actualizar',
        description: err instanceof Error ? err.message : 'Error inesperado',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={`Acciones para ${item.codigo}`}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="w-4 h-4" /> Editar nombre
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void toggle()}>
          {item.activo ? (
            <>
              <ToggleLeft className="w-4 h-4" /> Desactivar
            </>
          ) : (
            <>
              <ToggleRight className="w-4 h-4" /> Reactivar
            </>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* -------------------------------------------------------------------------- */
/* Dialogs                                                                    */
/* -------------------------------------------------------------------------- */

interface CreateItemDialogProps {
  kind: CatalogKind;
  open: boolean;
  onOpenChange(open: boolean): void;
}

function CreateItemDialog({ kind, open, onOpenChange }: CreateItemDialogProps): JSX.Element {
  const [codigo, setCodigo] = useState<string>('');
  const [nombre, setNombre] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const create = useCreateCatalogItem(kind);
  const { showToast } = useToast();

  const titleSingular = kind === 'insumo' ? 'insumo' : 'medicamento';
  const codigoPrefix = kind === 'insumo' ? 'INS-' : 'MED-';

  const handleClose = (): void => {
    if (create.isPending) return;
    setCodigo('');
    setNombre('');
    setError(null);
    onOpenChange(false);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    const trimmedCodigo = codigo.trim().toUpperCase();
    const trimmedNombre = nombre.trim();
    if (trimmedCodigo.length < 2) {
      setError('El código debe tener al menos 2 caracteres.');
      return;
    }
    if (trimmedNombre.length < 2) {
      setError('El nombre debe tener al menos 2 caracteres.');
      return;
    }
    try {
      const created = await create.mutateAsync({
        codigo: trimmedCodigo,
        nombre: trimmedNombre,
      });
      showToast({
        kind: 'success',
        title: `${kind === 'insumo' ? 'Insumo' : 'Medicamento'} creado`,
        description: `${created.codigo} · ${created.nombre}`,
      });
      handleClose();
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 409) {
        setError(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Error inesperado');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo {titleSingular}</DialogTitle>
          <DialogDescription>
            El código es la clave referencial y NO se puede cambiar después de crear.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="codigo">
              Código <span className="text-danger">*</span>
            </Label>
            <Input
              id="codigo"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              placeholder={`${codigoPrefix}071`}
              autoFocus
              maxLength={20}
              autoComplete="off"
            />
            <div className="text-[11px] text-text-muted">
              Solo letras, números y guiones. Convención sugerida:{' '}
              <code className="bg-surface-alt px-1 rounded">{codigoPrefix}NNN</code>.
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="nombre">
              Nombre <span className="text-danger">*</span>
            </Label>
            <Input
              id="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder={
                kind === 'insumo' ? 'Gasa estéril paquete 10x10' : 'Propofol 10 mg/ml amp 20 ml'
              }
              maxLength={200}
            />
          </div>

          {error !== null && (
            <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={create.isPending}>
            <X className="w-4 h-4" /> Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={create.isPending}>
            {create.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Creando…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Crear
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface EditItemDialogProps {
  kind: CatalogKind;
  item: CatalogItem;
  open: boolean;
  onOpenChange(open: boolean): void;
}

function EditItemDialog({ kind, item, open, onOpenChange }: EditItemDialogProps): JSX.Element {
  const [nombre, setNombre] = useState<string>(item.nombre);
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateCatalogItem(kind);
  const { showToast } = useToast();

  const handleClose = (): void => {
    if (update.isPending) return;
    setError(null);
    onOpenChange(false);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    const trimmed = nombre.trim();
    if (trimmed.length < 2) {
      setError('El nombre debe tener al menos 2 caracteres.');
      return;
    }
    if (trimmed === item.nombre) {
      handleClose();
      return;
    }
    try {
      await update.mutateAsync({ codigo: item.codigo, body: { nombre: trimmed } });
      showToast({
        kind: 'success',
        title: 'Ítem actualizado',
        description: `${item.codigo} · ${trimmed}`,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error inesperado');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar {kind === 'insumo' ? 'insumo' : 'medicamento'}</DialogTitle>
          <DialogDescription>
            El código <code className="bg-surface-alt px-1 rounded">{item.codigo}</code> es
            inmutable. Solo el nombre se puede modificar; para retirar del uso clínico,
            desactiva desde el menú.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <div className="flex flex-col gap-1.5">
            <Label>Código</Label>
            <Input value={item.codigo} disabled />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-nombre">
              Nombre <span className="text-danger">*</span>
            </Label>
            <Input
              id="edit-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={200}
              autoFocus
            />
          </div>

          {error !== null && (
            <div className="px-3 py-2 rounded-md bg-danger-soft text-red-800 text-sm border border-red-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={update.isPending}>
            <X className="w-4 h-4" /> Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={update.isPending}>
            {update.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Guardando…
              </>
            ) : (
              <>
                <Save className="w-4 h-4" /> Guardar cambios
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export para mantener simple el import en router.tsx
export type { ItemKind };
